import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp, Eye, Heart, Star, Clock, Filter, X, Package, Wallet, ArrowLeftRight, CheckCircle, AlertCircle, Image as ImageIcon, Box, ChevronDown, Info, Shirt, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePieces } from '../hooks/usePieces.js';
import { 
  getPopularPieces, 
  getNewPieces, 
  formatPrice, 
  getCategoryDisplayName 
} from '../utils/pieceUtils';
import { calculateSaleFeeWithUSD, logSaleFeeTransaction } from '../utils/saleFeeUtils';
// ModelPreview removed - no longer using 3D models
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import RequestTradeModal from '../components/RequestTradeModal';
import { useWallet } from '../contexts/WalletContext';
import { useActivity } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { completePurchaseWithDelegate, checkSufficientBalance, canWalletTransact } from '../utils/marketplaceTransactions';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { useFavorites } from '../hooks/useFavorites';
import { useWalletAction } from '../hooks/useWalletAction';
import WalletConnectionModal from '../components/WalletConnectionModal';
import { normalizePieces } from '../utils/pieceNormalizer.js';
import ModelViewer from '../components/ModelViewer.jsx';
import UnifiedPieceCard from '../components/UnifiedPieceCard';
import { getAllCapsules, getTrendingCapsules, getCapsuleForPiece } from '../utils/supabaseDatabase';
import { getCreatorProfileById } from '../utils/profileManagement';
import { useTopCreators } from '../hooks/useTopCreators';
import { trackPieceView, getTrendingPieces as getSupabaseTrendingPieces } from '../utils/supabaseViewTracking';
import { tradeEvents } from '../utils/tradeEvents';
import VerifiedBadge from '../components/VerifiedBadge';
import { createOffer } from '../utils/offersContract';
import { supabase } from '../lib/supabase.js';
import {
  createListing,
  purchaseListing,
  cancelListing,
  getActiveListing,
  canListPiece,
  canPurchaseListing,
  subscribeToListing,
  subscribeToPiece,
} from '../utils/resaleMarketplace';
// Edition-aware purchase function (auto-selects available editions)
import { purchasePiece, checkPurchaseEligibility } from '../utils/editionPurchase';
// Edition-aware trending pieces query
import { getTrendingPieces as getEditionAwareTrendingPieces } from '../utils/marketplaceQueries';
// Frontend piece grouping for better UI display
import { createGroupedPieceCards, isGroupedCard, getGroupLabel, getAvailablePieceFromGroup, getIndividualPieces } from '../utils/pieceGrouping';
import GroupedPiecesModal from '../components/GroupedPiecesModal';

const Explore = () => {
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, connectWallet, wallet } = useWallet();
  const { logSaleFeeCollected } = useActivity();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  
  // Debug: Log SOL price
  useEffect(() => {
    console.log('💵 Current SOL price in USD:', solPrice);
  }, [solPrice]);
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { withWalletCheck, showWalletModal, walletModalMessage, closeWalletModal } = useWalletAction();
  
  // Use Supabase data with pagination for infinite scroll
  const [piecesPage, setPiecesPage] = useState(0);
  const [allLoadedPieces, setAllLoadedPieces] = useState([]);
  const [hasMorePieces, setHasMorePieces] = useState(true);
  const { pieces, loading: piecesLoading, error: piecesError, refetch: refetchPieces, pagination } = usePieces(null, piecesPage, 50);
  const { creators: topCreators, loading: creatorsLoading, error: creatorsError } = useTopCreators(15, 0);
  const [popularPieces, setPopularPieces] = useState([]);
  const [newPieces, setNewPieces] = useState([]);
  const [trendingPieces, setTrendingPieces] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'popular', 'new', 'trending'
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  
  // Track which piece was last viewed to prevent double-tracking
  const lastTrackedPiece = useRef(null);
  
  // Grouped pieces modal state
  const [groupedPiecesModal, setGroupedPiecesModal] = useState({ isOpen: false, groupedCard: null });
  const [activeTab, setActiveTab] = useState('pieces'); // 'pieces', 'capsules', 'activity'
  const [capsules, setCapsules] = useState([]);
  const [capsulesLoading, setCapsulesLoading] = useState(true);
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null); // Track capsule info for current piece
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ 
    isOpen: false, 
    item: null, 
    offerAmount: '', 
    expirationDays: 7, 
    expirationHours: 0 
  });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [notForSaleModal, setNotForSaleModal] = useState({ isOpen: false, item: null });
  const [cancelListingModal, setCancelListingModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Resale marketplace state
  const [activeListing, setActiveListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showPurchaseConfirmModal, setShowPurchaseConfirmModal] = useState(false);
  const [listPrice, setListPrice] = useState('');
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  
  // Dropdown states
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  
  // Trending pieces pagination state
  const [trendingPage, setTrendingPage] = useState(0);
  
  // Featured capsules pagination state
  const [capsulePage, setCapsulePage] = useState(0);
  
  // Top creators pagination state
  const [creatorPage, setCreatorPage] = useState(0);
  
  // Notification system
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  
  // Dropdown refs
  const categoryDropdownRef = useRef(null);
  const priceDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Dropdown options
  const categoryOptions = [
    { id: 'all', name: 'All Categories' },
    { id: 'tops', name: 'Tops' },
    { id: 'bottoms', name: 'Bottoms' },
    { id: 'shoes', name: 'Shoes' },
    { id: 'accessories', name: 'Accessories' }
  ];

  const priceOptions = [
    { id: 'all', name: 'Any Price' },
    { id: '0-1', name: 'Under 1 SOL' },
    { id: '1-5', name: '1-5 SOL' },
    { id: '5-10', name: '5-10 SOL' },
    { id: '10+', name: '10+ SOL' }
  ];

  const sortOptions = [
    { id: 'newest', name: 'Newest' },
    { id: 'trending', name: 'Trending' },
    { id: 'most-viewed', name: 'Most Viewed' },
    { id: 'price-low', name: 'Price (Low → High)' },
    { id: 'price-high', name: 'Price (High → Low)' }
  ];

  // Mock capsule data
  const mockCapsules = [
    {
      id: 'c1',
      name: 'Streetwear Essentials',
      description: 'Complete streetwear collection with hoodie, pants, and sneakers',
      price: 2.5,
      pieces: 3,
      rarity: 'Rare',
      views: 1200,
      likes: 85,
      downloads: 45,
      sold: 12,
      totalMade: 50,
      quantity: 1,
      left: 38,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
      capsuleNumber: '#1234'
    },
    {
      id: 'c2',
      name: 'Summer Vibes',
      description: 'Perfect summer outfit with tank top, shorts, and sandals',
      price: 1.2,
      pieces: 4,
      rarity: 'Uncommon',
      views: 950,
      likes: 67,
      downloads: 32,
      sold: 8,
      totalMade: 100,
      quantity: 1,
      left: 25,
      status: 'Active',
      createdAt: new Date('2024-01-10'),
      image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&h=300&fit=crop',
      capsuleNumber: '#892'
    },
    {
      id: 'c3',
      name: 'Formal Collection',
      description: 'Elegant formal wear including blazer, dress pants, and dress shoes',
      price: 4.2,
      pieces: 5,
      rarity: 'Rare',
      views: 2100,
      likes: 156,
      downloads: 78,
      sold: 25,
      totalMade: 30,
      quantity: 1,
      left: 5,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 3 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      capsuleNumber: '#5678'
    },
    {
      id: 'c4',
      name: 'Athletic Gear',
      description: 'Complete athletic outfit with performance wear and sneakers',
      price: 3.1,
      pieces: 6,
      rarity: 'Uncommon',
      views: 1800,
      likes: 124,
      downloads: 58,
      sold: 15,
      totalMade: 75,
      quantity: 1,
      left: 60,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 10 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      capsuleNumber: '#9012'
    },
    {
      id: 'c5',
      name: 'Casual Friday',
      description: 'Relaxed office casual with polo, chinos, and loafers',
      price: 2.0,
      pieces: 4,
      rarity: 'Common',
      views: 750,
      likes: 45,
      downloads: 22,
      sold: 5,
      totalMade: 200,
      quantity: 1,
      left: 195,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 21 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=300&fit=crop',
      capsuleNumber: '#3456'
    },
    {
      id: 'c6',
      name: 'Winter Collection',
      description: 'Cozy winter essentials with jacket, sweater, and boots',
      price: 3.8,
      pieces: 5,
      rarity: 'Rare',
      views: 1650,
      likes: 98,
      downloads: 42,
      sold: 18,
      totalMade: 40,
      quantity: 1,
      left: 22,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
      capsuleNumber: '#7890'
    },
    {
      id: 'c7',
      name: 'Vintage Vibes',
      description: 'Retro-inspired collection with vintage jacket, jeans, and sneakers',
      price: 2.7,
      pieces: 4,
      rarity: 'Uncommon',
      views: 1100,
      likes: 72,
      downloads: 35,
      sold: 12,
      totalMade: 60,
      quantity: 1,
      left: 48,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 5 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=300&fit=crop',
      capsuleNumber: '#2468'
    },
    {
      id: 'c8',
      name: 'Tech Wear',
      description: 'Futuristic tech-inspired clothing with smart features',
      price: 4.5,
      pieces: 6,
      rarity: 'Rare',
      views: 2200,
      likes: 167,
      downloads: 89,
      sold: 28,
      totalMade: 35,
      quantity: 1,
      left: 7,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 2 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      capsuleNumber: '#1357'
    },
    {
      id: 'c9',
      name: 'Beach Party',
      description: 'Tropical beach wear with swimwear, cover-ups, and sandals',
      price: 1.8,
      pieces: 3,
      rarity: 'Common',
      views: 850,
      likes: 58,
      downloads: 28,
      sold: 7,
      totalMade: 150,
      quantity: 1,
      left: 143,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 18 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&h=300&fit=crop',
      capsuleNumber: '#9753'
    },
    {
      id: 'c10',
      name: 'Gothic Style',
      description: 'Dark aesthetic collection with gothic elements and accessories',
      price: 3.2,
      pieces: 5,
      rarity: 'Rare',
      views: 1400,
      likes: 89,
      downloads: 45,
      sold: 16,
      totalMade: 45,
      quantity: 1,
      left: 29,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 12 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      capsuleNumber: '#8642'
    },
    {
      id: 'c11',
      name: 'Minimalist',
      description: 'Clean, simple designs with neutral colors and basic pieces',
      price: 2.1,
      pieces: 4,
      rarity: 'Uncommon',
      views: 980,
      likes: 64,
      downloads: 31,
      sold: 9,
      totalMade: 80,
      quantity: 1,
      left: 71,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 8 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
      capsuleNumber: '#1597'
    },
    {
      id: 'c12',
      name: 'Artistic Expression',
      description: 'Bold, creative designs with unique patterns and colors',
      price: 3.9,
      pieces: 7,
      rarity: 'Rare',
      views: 1950,
      likes: 134,
      downloads: 67,
      sold: 22,
      totalMade: 25,
      quantity: 1,
      left: 3,
      status: 'Active',
      createdAt: new Date(Date.now() - Math.random() * 1 * 24 * 60 * 60 * 1000).toISOString(),
      image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=300&fit=crop',
      capsuleNumber: '#7531'
    }
  ];


  // Load trending capsules from Supabase
  const loadCapsules = async () => {
    try {
      setCapsulesLoading(true);
      console.log('🔄 Explore: Loading trending capsules from Supabase...');
      const trendingCapsulesData = await getTrendingCapsules(50);
      console.log('✅ Explore: Loaded trending capsules from Supabase:', trendingCapsulesData);
      console.log('📊 Explore: Number of trending capsules:', trendingCapsulesData?.length || 0);
      
      if (trendingCapsulesData && trendingCapsulesData.length > 0) {
        console.log('🎯 Explore: Using trending capsules');
        setCapsules(trendingCapsulesData);
      } else {
        console.log('⚠️ Explore: No capsules found, using mock data');
        setCapsules(mockCapsules);
      }
    } catch (error) {
      console.error('❌ Explore: Error loading trending capsules:', error);
      console.log('🔄 Explore: Falling back to mock data');
      // Fallback to mock data if Supabase fails
      setCapsules(mockCapsules);
    } finally {
      setCapsulesLoading(false);
    }
  };

  // Unified transformation function for all pieces (regular and trending)
  const transformPieceWithCreatorProfile = (piece) => {
    console.log('Transforming piece:', piece.name);
    console.log('  thumbnailUrl:', piece.thumbnailUrl);
    console.log('  creator data:', piece.creator);
    
    // Handle both nested creator object (from regular pieces) and flattened fields (from trending view)
    const creatorHandle = piece.creator?.handle || piece.creator_handle || piece.creatorName || 'Creator';
    const creatorWallet = piece.creator?.wallet || piece.creator_wallet || null;
    const isVerified = piece.creator?.is_verified || piece.creator_verified || piece.isVerified || false;
    const profilePictureUrl = piece.creator?.profile_picture_url || piece.creator_profile_picture || null;
    const creatorPiecesCount = piece.creator?.total_pieces_created || piece.creator_total_pieces_created || 0;
    
    return {
      ...piece, // Spread normalized piece data
      id: `supabase_${piece.id}`, // Prefix to avoid key conflicts
      views: piece.total_views || piece.views || Math.floor(Math.random() * 5000) + 100,
      likes: Math.floor(Math.random() * 500) + 10,
      downloads: Math.floor(Math.random() * 200) + 5,
      sold: Math.floor(Math.random() * piece.totalMade * 0.8),
      originalCreator: {
        name: creatorHandle,
        username: creatorHandle,
        wallet: creatorWallet,
        piecesCount: creatorPiecesCount,
        verified: isVerified,
        profilePicture: profilePictureUrl,
        profile_picture_url: profilePictureUrl
      }
    };
  };

  // Load pieces data with creator profiles
  const loadPiecesWithCreatorProfiles = async () => {
    // Use the normalizePieces function to standardize field names
    const normalizedPieces = normalizePieces(pieces);
    
    // Transform pieces with creator data using unified function
    const piecesWithCreatorProfiles = normalizedPieces.map(transformPieceWithCreatorProfile);

    return piecesWithCreatorProfiles;
  };

  // Load pieces data and accumulate for infinite scroll
  useEffect(() => {
    const loadPieces = async () => {
      if (!pieces || pieces.length === 0) return;
      
      const transformedPieces = await loadPiecesWithCreatorProfiles();

      console.log(`Loading page ${piecesPage}, received ${transformedPieces.length} pieces`);
      
      // Accumulate pieces for infinite scroll
      setAllLoadedPieces(prevPieces => {
        // If it's the first page, replace. Otherwise, append
        if (piecesPage === 0) {
          return transformedPieces;
        } else {
          // Deduplicate by ID
          const existingIds = new Set(prevPieces.map(p => p.id));
          const newUniquePieces = transformedPieces.filter(p => !existingIds.has(p.id));
          return [...prevPieces, ...newUniquePieces];
        }
      });
      
      // Check if there are more pages
      if (pagination) {
        const hasMore = (piecesPage + 1) < pagination.totalPages;
        setHasMorePieces(hasMore);
        console.log(`Has more pieces: ${hasMore}, current page: ${piecesPage}, total pages: ${pagination.totalPages}`);
      }
      
      setIsLoadingMore(false);
      
      // Only calculate popular/new/trending on first load
      if (piecesPage === 0 && transformedPieces.length > 0) {
        // Apply frontend grouping to reduce visual duplication
        console.log('🔄 Applying frontend piece grouping...');
        
        const popularPieces = createGroupedPieceCards(getPopularPieces(transformedPieces));
        const newPieces = createGroupedPieceCards(getNewPieces(transformedPieces));
        
        // Get trending pieces from Supabase view tracking system (edition-aware)
        console.log('📊 Loading trending pieces from Supabase (edition-aware)...');
        const trendingPiecesRaw = await getEditionAwareTrendingPieces(100);
        
        console.log('📊 Trending Analysis:');
        console.log('  - Trending pieces from Supabase (masters only):', trendingPiecesRaw.length);
        
        if (trendingPiecesRaw.length > 0) {
          console.log('  - Top trending piece:', trendingPiecesRaw[0]?.name);
          console.log('  - Top trending score:', trendingPiecesRaw[0]?.trending_score);
          console.log('  - Views (24h / 7d / total):', 
            trendingPiecesRaw[0]?.views_24h, '/', 
            trendingPiecesRaw[0]?.views_7d, '/', 
            trendingPiecesRaw[0]?.total_views
          );
        }
        
        if (trendingPiecesRaw.length === 0) {
          console.log('  ℹ️ No trending pieces yet (no views tracked)');
        } else {
          console.log('  ✅ Showing pieces sorted by trending score');
        }
        
        // Normalize trending pieces to convert snake_case to camelCase (thumbnail_url -> thumbnailUrl)
        const trendingPiecesNormalized = normalizePieces(trendingPiecesRaw).map(transformPieceWithCreatorProfile);
        const trendingPieces = createGroupedPieceCards(trendingPiecesNormalized);
        
        console.log('Popular pieces (grouped):', popularPieces.length);
        console.log('New pieces (grouped):', newPieces.length);
        console.log('Trending pieces (grouped):', trendingPieces.length);
        
        setPopularPieces(popularPieces);
        setNewPieces(newPieces);
        setTrendingPieces(trendingPieces);
        
        // Load capsules from Supabase
        loadCapsules();
      }
    };

    loadPieces();
  }, [pieces, piecesPage]);


  // Listen for trade events and refetch pieces when trades complete
  useEffect(() => {
    console.log('📢 Explore: Setting up trade event listener');
    const unsubscribe = tradeEvents.subscribe((tradeData) => {
      console.log('📢 Explore: Trade event received, refetching pieces...', tradeData);
      // Reset to first page and refetch
      setPiecesPage(0);
      setAllLoadedPieces([]);
      refetchPieces();
    });

    return () => {
      console.log('📢 Explore: Cleaning up trade event listener');
      unsubscribe();
    };
  }, [refetchPieces]);

  // Reset pagination when filters change
  useEffect(() => {
    setPiecesPage(0);
    setAllLoadedPieces([]);
    setHasMorePieces(true);
  }, [searchQuery, categoryFilter, priceFilter, verifiedOnly, sortBy, activeTab]);

  // Infinite scroll: Load more pieces when scrolling near bottom
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMorePieces && !isLoadingMore && !piecesLoading && activeTab === 'pieces') {
          console.log('🔄 Loading more pieces...');
          setIsLoadingMore(true);
          setPiecesPage(prev => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before reaching the bottom
        threshold: 0.1
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMorePieces, isLoadingMore, piecesLoading, activeTab]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(event.target)) {
        setIsPriceDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch active listing when piece detail modal opens
  useEffect(() => {
    const fetchActiveListing = async () => {
      if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
        setActiveListing(null);
        return;
      }

      try {
        setListingLoading(true);
        const listing = await getActiveListing(pieceDetailModal.item.mint_address);
        setActiveListing(listing);
      } catch (error) {
        console.error('Error fetching active listing:', error);
        setActiveListing(null);
      } finally {
        setListingLoading(false);
      }
    };

    fetchActiveListing();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Real-time subscription for listing and piece changes
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
      return;
    }
    
    console.log('👁️ Setting up real-time subscriptions for:', pieceDetailModal.item.mint_address);
    
    // Subscribe to listing changes
    const listingSubscription = subscribeToListing(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Listing update received:', payload);
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        if (payload.new.status === 'active') {
          setActiveListing(payload.new);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'listed',
              listing_price: payload.new.price_sol
            }
          }));
        } else if (payload.new.status === 'filled') {
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'sold',
              holder_wallet: payload.new.buyer_wallet
            }
          }));
        } else if (payload.new.status === 'canceled') {
          // Listing canceled - remove buy button
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'unlisted',
              listing_price: null
            }
          }));
        }
      } else if (payload.eventType === 'DELETE') {
        setActiveListing(null);
      }
    });
    
    // Subscribe to piece changes
    const pieceSubscription = subscribeToPiece(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Piece update received:', payload);
      
      if (payload.eventType === 'UPDATE') {
        setPieceDetailModal(prev => ({
          ...prev,
          item: {
            ...prev.item,
            ...payload.new
          }
        }));
      }
    });
    
    // Cleanup subscriptions
    return () => {
      console.log('🔌 Cleaning up subscriptions...');
      listingSubscription.unsubscribe();
      pieceSubscription.unsubscribe();
    };
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Handle dropdown changes
  const handleCategoryChange = (optionId) => {
    setCategoryFilter(optionId);
    setIsCategoryDropdownOpen(false);
  };

  const handlePriceChange = (optionId) => {
    setPriceFilter(optionId);
    setIsPriceDropdownOpen(false);
  };

  const handleSortChange = (optionId) => {
    setSortBy(optionId);
    setIsSortDropdownOpen(false);
  };

  // Get filtered and sorted items based on active tab and filters
  const getFilteredItems = () => {
    // Use the combined processed pieces data instead of raw Supabase data
    let items;
    
    if (activeTab === 'pieces') {
      // Use accumulated pieces from pagination for "All Pieces" section
      // Apply frontend grouping to reduce visual duplication and show edition badges
      items = createGroupedPieceCards(allLoadedPieces || []);
    } else {
      items = capsules || [];
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.originalCreator?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.creator?.username?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply category filter (only for pieces)
    if (activeTab === 'pieces' && categoryFilter !== 'all') {
      items = items.filter(item => item.category?.toLowerCase() === categoryFilter.toLowerCase());
    }
    
    // Apply price filter
    if (priceFilter !== 'all') {
      items = items.filter(item => {
        const price = parseFloat(item.price);
        switch (priceFilter) {
          case '0-1':
            return price < 1;
          case '1-5':
            return price >= 1 && price <= 5;
          case '5-10':
            return price > 5 && price <= 10;
          case '10+':
            return price > 10;
          default:
            return true;
        }
      });
    }
    
    // Apply verified filter
    if (verifiedOnly) {
      items = items.filter(item => 
        item.originalCreator?.verified || item.creator?.verified
      );
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'newest':
        items = [...items].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'trending':
        items = [...items].sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      case 'most-viewed':
        items = [...items].sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      case 'price-low':
        items = [...items].sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
        break;
      case 'price-high':
        items = [...items].sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
        break;
      default:
        break;
    }
    
    return items;
  };

  // Handle capsule view
  const handleViewCapsuleFromCard = (capsule) => {
    // Navigate to capsule page instead of showing modal
    navigate(`/capsule/${capsule.id}`);
  };

  // Handle buy piece
  const handleBuyPiece = (piece) => {
    // TODO: Implement actual buy functionality with wallet integration
    console.log('Buying piece:', piece.name, 'for', piece.price, 'SOL');
    showNotification(`Buying ${piece.name} for ${piece.price} SOL (≈ $${(piece.price * solPrice).toFixed(2)} USD)`, 'success');
  };

  // Handle piece detail modal
  const openPieceDetailModal = async (item) => {
    console.log('🔍 openPieceDetailModal: Opening modal for item:', item);
    console.log('🔍 Item price data:', {
      id: item?.id,
      name: item?.name,
      price: item?.price,
      listing_price: item?.listing_price,
      lowestPrice: item?.lowestPrice,
      isGroup: isGroupedCard(item),
      groupSize: item?._groupSize
    });
    
    // Check if this is a grouped card - if so, show grouped pieces modal instead
    if (isGroupedCard(item)) {
      console.log('🔗 Grouped card detected - showing grouped pieces modal');
      console.log(`  - Group size: ${item._groupSize}`);
      console.log(`  - Available: ${item._availableCount}`);
      setGroupedPiecesModal({ isOpen: true, groupedCard: item });
      return;
    }
    
    // Refetch fresh piece data from database to ensure we have complete data including price
    try {
      const { data: freshPiece, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            profile_picture_url,
            banner_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .eq('id', item.id)
        .single();
      
      if (!error && freshPiece) {
        console.log('✅ Refetched fresh piece data:', {
          id: freshPiece.id,
          name: freshPiece.name,
          price: freshPiece.price,
          listing_price: freshPiece.listing_price,
          royalty_bps: freshPiece.royalty_bps,
          HAS_ROYALTY_BPS: 'royalty_bps' in freshPiece
        });
        // Merge fresh data with existing item data
        const mergedItem = { ...item, ...freshPiece };
        // Normalize the piece data to ensure consistent field names
        const normalizedItem = normalizePieces([mergedItem])[0];
        setPieceDetailModal({ isOpen: true, item: normalizedItem });
      } else {
        console.warn('⚠️ Could not refetch piece data, using existing:', error);
        // Normalize the item to ensure consistent field names
        const normalizedItem = normalizePieces([item])[0];
        setPieceDetailModal({ isOpen: true, item: normalizedItem });
      }
    } catch (error) {
      console.error('❌ Error refetching piece:', error);
      // Normalize the item to ensure consistent field names
      const normalizedItem = normalizePieces([item])[0];
      setPieceDetailModal({ isOpen: true, item: normalizedItem });
    }
    
    // Track view using Supabase Edge Function (only if not already tracked in this session)
    if (item?.id && lastTrackedPiece.current !== String(item.id)) {
      const walletAddress = publicKey?.toString() || null;
      // Convert piece ID to string to ensure compatibility
      const pieceIdString = String(item.id);
      const result = await trackPieceView(pieceIdString, walletAddress);
      if (result.success) {
        console.log('✅ View tracked via Supabase:', result.stats);
        lastTrackedPiece.current = pieceIdString;
      } else {
        console.log('⏭️ View not tracked:', result.error);
        // Still mark as tracked to avoid repeated failed attempts
        lastTrackedPiece.current = pieceIdString;
      }
    }
    
    // Check if piece has 3D model and set initial view mode
    const normalizedPiece = item ? normalizePieces([item])[0] : null;
    const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
    
    if (has3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
    }

    // Check if piece belongs to a capsule
    if (item?.id) {
      (async () => {
        try {
          // Extract original ID from prefixed ID (supabase_123 -> 123, local_456_0 -> 456)
          let originalId = item.id;
          const itemIdString = String(item.id);
          console.log('🔍 CAPSULE CHECK: Starting check for piece ID:', item.id);
          
          if (itemIdString.startsWith('supabase_')) {
            originalId = itemIdString.replace('supabase_', '');
            // Convert to number if it's a numeric ID
            if (!isNaN(originalId)) {
              originalId = parseInt(originalId, 10);
            }
          } else if (itemIdString.startsWith('local_')) {
            // For local pieces, they typically don't belong to capsules
            console.log('🔍 Local piece detected, skipping capsule check');
            setPieceCapsuleInfo(null);
            return;
          }
          
          console.log('🔍 Checking capsule for piece:', { originalId, transformedId: item.id, originalIdType: typeof originalId });
          const capsuleInfo = await getCapsuleForPiece(originalId);
          console.log('🔍 Capsule info result:', capsuleInfo);
          
          if (capsuleInfo) {
            console.log('✅ CAPSULE FOUND! Setting pieceCapsuleInfo:', capsuleInfo);
          } else {
            console.log('ℹ️ No capsule found for this piece');
          }
          
          setPieceCapsuleInfo(capsuleInfo);
        } catch (error) {
          console.error('❌ Error fetching capsule info for piece:', error);
          setPieceCapsuleInfo(null);
        }
      })();
    } else {
      console.log('⚠️ No piece ID found, cannot check capsule');
      setPieceCapsuleInfo(null);
    }
    
    // Load active listing if piece is listed
    if (item?.listing_status === 'listed' || item?.is_delegate_active) {
      console.log('📋 Loading active listing for piece:', {
        mint_address: item?.mint_address,
        listing_status: item?.listing_status,
        is_delegate_active: item?.is_delegate_active,
        piece_id: item?.id
      });
      
      try {
        // Try to fetch listing by mint address first
        let listing = null;
        if (item?.mint_address) {
          listing = await getActiveListing(item.mint_address);
          console.log('✅ Listing loaded by mint:', listing);
        }
        
        // FALLBACK: If no listing found by mint, query database directly by piece_id
        if (!listing && item?.id) {
          // Extract numeric ID from prefixed ID (supabase_10601 -> 10601)
          let numericPieceId = item.id;
          const itemIdString = String(item.id);
          if (itemIdString.startsWith('supabase_')) {
            numericPieceId = itemIdString.replace('supabase_', '');
            // Convert to number if it's a numeric ID
            if (!isNaN(numericPieceId)) {
              numericPieceId = parseInt(numericPieceId, 10);
            }
          } else if (itemIdString.startsWith('local_')) {
            console.log('📋 Local piece detected, skipping database fallback');
            numericPieceId = null;
          }
          
          if (numericPieceId) {
            console.log('📋 Trying database fallback for numeric piece_id:', numericPieceId);
            const { data: dbListing, error } = await supabase
              .from('listings')
              .select('*')
              .eq('piece_id', numericPieceId)
              .eq('status', 'active')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (!error && dbListing) {
              console.log('✅ Listing loaded from database:', dbListing);
              listing = dbListing;
            } else if (error) {
              console.error('❌ Database query error:', error);
            }
          }
        }
        
        setActiveListing(listing);
        if (!listing) {
          console.warn('⚠️ No active listing found for this piece');
        }
      } catch (error) {
        console.error('❌ Failed to load listing:', error);
        setActiveListing(null);
      }
    } else {
      console.log('📋 No active listing (status not listed)');
      setActiveListing(null);
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setModalViewMode('3d');
    setThreeDStatus('loading');
    setThreeDError(null);
    setPieceCapsuleInfo(null);
    setActivityPanelOpen(false);
    // Clear resale state
    setActiveListing(null);
    setShowListModal(false);
    setListPrice('');
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle holder button click - navigate to holder profile
  const handleHolderClick = (item) => {
    const holderWallet = item?.holder?.wallet || item?.holder_wallet;
    if (holderWallet) {
      navigate(`/creator/${holderWallet}`);
    }
  };

  // Handle view entire capsule button click
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo?.id) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
    }
  };

  // Test function to manually check capsule info (for debugging)
  const testCapsuleCheck = async (pieceId) => {
    try {
      console.log('🧪 Testing capsule check for piece ID:', pieceId);
      const result = await getCapsuleForPiece(pieceId);
      console.log('🧪 Test result:', result);
      return result;
    } catch (error) {
      console.error('🧪 Test error:', error);
      return null;
    }
  };

  // Make test function available globally for debugging
  window.testCapsuleCheck = testCapsuleCheck;

  // Handle buy now button
  const handleBuyNow = withWalletCheck((item) => {
    // Open buy modal for primary sale (direct from creator)
    // Use the item passed from the card, or fall back to pieceDetailModal.item
    let targetItem = item || pieceDetailModal.item;
    
    // Check if this is a grouped card - if so, get an available piece from the group
    if (isGroupedCard(targetItem)) {
      console.log('🔗 Grouped card - selecting available piece from group');
      const availablePiece = getAvailablePieceFromGroup(targetItem);
      if (!availablePiece) {
        showNotification('No pieces available in this group', 'error');
        return;
      }
      targetItem = availablePiece;
      console.log('✅ Selected piece from group:', targetItem.id);
    }
    
    console.log('💰 Opening buy modal for item:', {
      id: targetItem?.id,
      name: targetItem?.name,
      price: targetItem?.price,
      listing_price: targetItem?.listing_price,
      lowestPrice: targetItem?.lowestPrice,
      listing_status: targetItem?.listing_status
    });
    setBuyModal({ isOpen: true, item: targetItem });
  }, 'make a purchase');

  // Process purchase with wallet - REAL BLOCKCHAIN TRANSACTION
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    // Check wallet capability
    if (!canWalletTransact(wallet)) {
      showNotification('Please use Phantom or Solflare wallet to make purchases.', 'error');
      return;
    }

    const { item } = buyModal;
    // Use listing_price first, fallback to price
    const price = parseFloat(item.listing_price || item.price || '0');
    
    if (!price || price <= 0) {
      showNotification('Cannot purchase piece with invalid price. Please refresh and try again.', 'error');
      closeBuyModal();
      return;
    }
    
    // Check sufficient balance (with buffer for gas fees)
    const balanceCheck = await checkSufficientBalance({ connected: isConnected, publicKey }, price);
    if (!balanceCheck.sufficient) {
      showNotification(`Not enough SOL. You need ${balanceCheck.required.toFixed(2)} SOL but only have ${balanceCheck.balance.toFixed(2)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Execute the complete purchase flow on blockchain using edition-aware purchase
      console.log(`🛒 Purchasing ${item.name} for ${price} SOL...`);
      console.log(`🎨 Edition system: Auto-selecting available edition if multi-edition piece`);
      
      // Use edition-aware purchase (automatically selects available edition)
      const result = await purchasePiece(
        wallet,
        item,
        price
      );
      
      // Log edition info if available
      if (result.editionNumber && result.totalEditions) {
        console.log(`✅ Purchased edition ${result.editionNumber}/${result.totalEditions}`);
      }
      
      // Log sale fee activity
      logSaleFeeCollected({
        pieceId: item.id,
        pieceName: item.name,
        feeAmount: result.paymentTransaction?.feeBreakdown?.feeAmount || result.feeBreakdown?.feeAmount || 0,
        piecePrice: price,
        buyerWallet: publicKey.toString(),
        sellerWallet: item.holder_wallet || item.creators?.wallet || 'Unknown Seller',
        transactionHash: result.paymentTransaction?.signature || result.transactionSignature
      });
      
      // Show success message with transaction details
      const nftInfo = result.atomic ? 
        `\n\n🎨 NFT Transferred: Atomic transaction completed successfully!` : 
        result.fallback && result.authorized && result.directTransfer ? 
        `\n\n🎨 NFT Transfer Initiated: ${result.transferSignature.slice(0, 8)}...\nPayment completed and NFT transfer initiated!\nThe NFT should now be in your wallet.` :
        result.fallback && result.authorized ? 
        `\n\n🎨 NFT Transferred: ${result.transferSignature.slice(0, 8)}...\nPayment completed and NFT transferred using delegate authority!\nThe NFT should now be in your wallet.` :
        result.fallback && result.transferSignature && result.transferSignature.startsWith('FALLBACK') ? 
        `\n\n💾 Ownership Updated: ${result.transferSignature.slice(0, 8)}...\nPayment completed and ownership recorded in database!\n\n⚠️ NOTE: The NFT is still in the seller's wallet on-chain.\nThe seller needs to manually transfer it to your wallet.` :
        result.fallback && result.transferSignature ? 
        `\n\n💾 Ownership Updated: ${result.transferSignature.slice(0, 8)}...\nPayment completed and ownership recorded in database!\n\n⚠️ NOTE: The NFT is still in the seller's wallet on-chain.\nThe seller needs to manually transfer it to your wallet.` :
        result.fallback ? 
        `\n\n⚠️ IMPORTANT: Payment successful, but NFT transfer will be handled separately.\nAuction House not available - using fallback payment method.` :
        `\n\n⚠️ IMPORTANT: Payment successful, but NFT transfer may be pending.`;
      
      const feeBreakdown = result.paymentTransaction?.feeBreakdown || result.feeBreakdown || {};
      const paymentTx = result.paymentTransaction || { signature: result.transactionSignature, explorerUrl: '#' };
      
      // Calculate fee breakdown for Auction House
      const platformFee = price * 0.025; // 2.5%
      const sellerReceives = price - platformFee;
      
      showNotification(
        `Successfully purchased ${item.name} for ${price.toFixed(2)} SOL! 🎉`,
        'success'
      );
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // Reload pieces to show updated ownership
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      // Make error messages user-friendly
      let userMessage = 'Unable to complete purchase. Please try again.';
      if (error.message?.includes('insufficient')) {
        userMessage = 'Not enough SOL in your wallet to complete this purchase.';
      } else if (error.message?.includes('rejected') || error.message?.includes('declined')) {
        userMessage = 'Purchase cancelled. You declined the transaction.';
      } else if (error.message?.includes('delegate') || error.message?.includes('delegated')) {
        userMessage = 'This piece is not properly listed for sale. The seller needs to re-list it. Please try a different piece.';
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      showNotification(userMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Handle make offer button
  const handleMakeOffer = withWalletCheck(async (item) => {
    // Check if user already has an active offer on this piece
    try {
      const { getPieceOffers } = await import('../utils/offersContract');
      const offersResult = await getPieceOffers(item.mint_address, 'active');
      
      if (offersResult.success && offersResult.offers.length > 0) {
        // Check if any of the active offers are from this user
        const userOffer = offersResult.offers.find(
          offer => offer.offerer_wallet === publicKey?.toString()
        );
        
        if (userOffer) {
          showNotification(
            `You already have an active offer of ${userOffer.offer_price} SOL on this piece. Visit the Offers page to view or update it.`,
            'info'
          );
          return;
        }
      }
    } catch (error) {
      console.log('Could not check for existing offers:', error);
      // Continue anyway - the backend will catch duplicates
    }
    
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7, expirationHours: 0 });
  }, 'make an offer');

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle trade request submission
  const handleTradeRequestSubmit = (tradeRequest) => {
    console.log('Trade request submitted:', tradeRequest);
    // Close both the trade modal and the piece detail modal
    setRequestTradeModal({ isOpen: false, item: null });
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle trade request success
  const handleTradeRequestSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 4000);
  };

  // Auto-switch trending pieces sections every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTrendingPage((prevPage) => (prevPage + 1) % 3);
    }, 10000); // Switch every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-switch featured capsules sections every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCapsulePage((prevPage) => (prevPage + 1) % 3);
    }, 10000); // Switch every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-switch top creators sections every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCreatorPage((prevPage) => (prevPage + 1) % 3);
    }, 10000); // Switch every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Process offer with wallet
  const processOffer = async () => {
    console.log('processOffer called');
    console.log('isConnected:', isConnected);
    console.log('publicKey:', publicKey);
    console.log('offerModal:', offerModal);
    console.log('balance:', balance);
    
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays, expirationHours } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    console.log('offerPrice:', offerPrice);
    console.log('isNaN(offerPrice):', isNaN(offerPrice));
    console.log('offerPrice <= 0:', offerPrice <= 0);
    console.log('balance < offerPrice:', balance < offerPrice);
    
    if (isNaN(offerPrice) || offerPrice <= 0) {
      showNotification('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      showNotification(`Insufficient balance. You need ${offerPrice} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      showNotification('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      console.log(`Piece ID: ${item.id} -> ${realPieceId}`);
      console.log(`Expires in: ${expirationDays} days, ${expirationHours} hours`);
      console.log(`From wallet: ${publicKey.toString()}`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '', // Could add message field to modal if desired
        expirationDays: expirationDays,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          showNotification(
            'You already have an active offer on this piece. Visit the Offers page to view or update it.',
            'error'
          );
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      console.log('Offer ID:', result.offer.id);
      
      // Success!
      showNotification(`Offer of ${offerPrice} SOL submitted for ${item.name}! ✓`, 'success');
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
      
    } catch (error) {
      console.error('❌ Offer failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to submit offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
  };

  // ===== RESALE MARKETPLACE HANDLERS =====
  
  // Handle listing creation
  const handleCreateListing = async () => {
    const { item } = pieceDetailModal;
    const price = parseFloat(listPrice);
    
    if (!price || price <= 0) {
      showNotification('Please enter a valid price', 'error');
      return;
    }
    
    try {
      setListingLoading(true);
      console.log('📝 Creating listing for:', item.name, 'at', price, 'SOL');
      
      const result = await createListing(wallet, item, price);
      
      console.log('✅ Listing created:', result);
      showNotification(`Successfully listed ${item.name} for ${price} SOL!`, 'success');
      
      // Update local state
      setActiveListing(result.listing);
      setShowListModal(false);
      setListPrice('');
      
      // Refresh pieces to update UI
      refetchPieces();
      
    } catch (error) {
      console.error('❌ Listing failed:', error);
      
      // User-friendly error messages
      let errorMessage = 'Could not create listing. Please try again.';
      
      if (error.message?.includes('Already listed') || error.message?.includes('already listed')) {
        errorMessage = 'This item is already listed.';
      } else if (error.message?.includes('Only the current owner') || error.message?.includes('do not own')) {
        errorMessage = 'You can only list items you currently own.';
      } else if (error.message?.includes('not minted')) {
        errorMessage = 'This item must be minted before listing.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setListingLoading(false);
    }
  };

  // Handle purchase - open confirmation modal
  const handlePurchaseListing = withWalletCheck(() => {
    const { item } = pieceDetailModal;
    
    if (!activeListing) {
      showNotification('No active listing found', 'error');
      return;
    }
    
    // Open purchase confirmation modal
    setShowPurchaseConfirmModal(true);
  }, 'purchase this piece');

  // Confirm and process purchase
  const confirmPurchaseListing = async () => {
    const { item } = pieceDetailModal;
    
    if (!activeListing) {
      showNotification('No active listing found', 'error');
      return;
    }
    
    try {
      setListingLoading(true);
      console.log('🛒 Purchasing:', item.name);
      console.log('🎨 Edition system: Auto-selecting available edition if multi-edition piece');
      
      // Use edition-aware purchase (automatically selects available edition)
      const result = await purchasePiece(
        wallet, 
        item, 
        activeListing.price_sol
      );
      
      console.log('✅ Purchase successful:', result);
      
      // Log edition info if available
      const editionInfo = result.editionNumber && result.totalEditions 
        ? ` | Edition: ${result.editionNumber}/${result.totalEditions}` 
        : '';
      
      showNotification(
        `Successfully purchased ${item.name}!${editionInfo} 🎉`,
        'success'
      );
      
      setShowPurchaseConfirmModal(false);
      closePieceDetailModal();
      refetchPieces();
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      showNotification('Unable to complete purchase. Please try again.', 'error');
      setShowPurchaseConfirmModal(false);
    } finally {
      setListingLoading(false);
    }
  };

  // Handle cancel listing - show confirmation modal
  const handleCancelListing = () => {
    const { item } = pieceDetailModal;
    setCancelListingModal({ isOpen: true, item });
  };

  // Confirm cancel listing
  const confirmCancelListing = async () => {
    const { item } = cancelListingModal;
    
    try {
      setListingLoading(true);
      console.log('❌ Canceling listing...');
      
      const result = await cancelListing(wallet, activeListing, item);
      
      console.log('✅ Listing canceled:', result);
      showNotification('Listing canceled successfully!', 'success');
      
      setActiveListing(null);
      refetchPieces();
      setCancelListingModal({ isOpen: false, item: null });
      
    } catch (error) {
      console.error('❌ Cancel failed:', error);
      showNotification(`Failed to cancel: ${error.message}`, 'error');
    } finally {
      setListingLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };


  return (
    <div className="min-h-screen bg-black">
      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
               {/* Left side - Thumbnail/3D Model */}
               <div className="w-1/2 p-6 overflow-y-auto">
                 {/* 3D Model Toggle Buttons */}
                 {(() => {
                   const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                   const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                   
                   if (has3DModel) {
                     return (
                       <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                         <button
                           onClick={() => setModalViewMode('3d')}
                           className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                             modalViewMode === '3d'
                               ? 'text-white bg-[#2a2a2a] shadow-lg' 
                               : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                           }`}
                         >
                           3D Model
                         </button>
                         <button
                           onClick={() => setModalViewMode('thumbnail')}
                           className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                             modalViewMode === 'thumbnail'
                               ? 'text-white bg-[#2a2a2a] shadow-lg' 
                               : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                           }`}
                         >
                           Thumbnail
                         </button>
                       </div>
                     );
                   }
                   return null;
                 })()}
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                      const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        // You can customize orientation per model if needed
                        // Example: orientation="0deg 45deg 150%" for models that need different framing
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        // You can customize orientation per model if needed
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return pieceDetailModal.item?.thumbnailUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnailUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', pieceDetailModal.item.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                          <img
                            src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                            alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                          </div>
                          {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty - Outside button */}
                    {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links */}
                    {(() => {
                      // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                      const socialLinks = pieceCapsuleInfo 
                        ? {
                            website: pieceCapsuleInfo.website,
                            twitter: pieceCapsuleInfo.twitter,
                            instagram: pieceCapsuleInfo.instagram,
                            discord: pieceCapsuleInfo.discord
                          }
                        : (pieceDetailModal.item?.social_links || {});
                      const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      // DEBUG: Log social links data
                      console.log('🔍 [EXPLORE] Social Links Debug:', {
                        pieceId: pieceDetailModal.item?.id,
                        pieceName: pieceDetailModal.item?.name,
                        pieceCapsuleInfo: pieceCapsuleInfo,
                        usingCapsuleLinks: !!pieceCapsuleInfo,
                        social_links: socialLinks,
                        hasSocialLinks
                      });
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                          {socialLinks.website && (
                            <a
                              href={socialLinks.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Website"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="2" y1="12" x2="22" y2="12"/>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.twitter && (
                            <a
                              href={socialLinks.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="X (Twitter)"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.instagram && (
                            <a
                              href={socialLinks.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Instagram"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.discord && (
                            <a
                              href={socialLinks.discord}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Discord"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(pieceDetailModal.item.id);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(pieceDetailModal.item.id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(pieceDetailModal.item.id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Custom Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                        pieceDetailModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium">
                          {getCategoryDisplayName(pieceDetailModal.item?.category) || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{pieceDetailModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {(() => {
                            const item = pieceDetailModal.item;
                            const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            return displayPrice;
                          })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${(() => {
                          const item = pieceDetailModal.item;
                          const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                          return ((displayPrice || 0) * solPrice).toFixed(2);
                        })()} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {pieceDetailModal.item?.createdAt ? new Date(pieceDetailModal.item.createdAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {/* PRIMARY & SECONDARY MARKETPLACE BUTTONS */}
                    {(() => {
                      const isOwner = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      const isSeller = isConnected && publicKey && 
                        activeListing?.seller_wallet === publicKey.toString();
                      
                      // Check if piece is owned by the original creator (primary sale)
                      const creatorWallet = pieceDetailModal.item?.creator_wallet || 
                                           pieceDetailModal.item?.creators?.wallet ||
                                           pieceDetailModal.item?.originalCreator?.wallet;
                      const isOwnedByCreator = pieceDetailModal.item?.holder_wallet === creatorWallet;
                      
                      const listEligibility = canListPiece(wallet, pieceDetailModal.item);
                      const purchaseEligibility = canPurchaseListing(wallet, activeListing, pieceDetailModal.item);
                      
                      return (
                        <>
                          {/* OWNERSHIP BADGE: Show when user owns the piece */}
                          {isOwner && (
                            <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30 mb-2">
                              <span className="text-sm font-medium">✓ You own this piece</span>
                            </div>
                          )}
                          
                          {/* PRIMARY SALE: Buy directly from creator (no listing needed) */}
                          {!isOwner && isOwnedByCreator && 
                           pieceDetailModal.item?.mint_address && 
                           !activeListing &&
                           pieceDetailModal.item?.listing_status !== 'sold' && (
                            <button
                              onClick={() => handleBuyNow(pieceDetailModal.item)}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                            >
                              Buy Now - {(pieceDetailModal.item?.listing_price || pieceDetailModal.item?.price || 0).toFixed(4)} SOL
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Owner can list for resale (including relisting after purchase) */}
                          {isOwner && !activeListing && pieceDetailModal.item?.listing_status !== 'listed' && pieceDetailModal.item?.listing_status !== 'sold' && (
                            <button
                              onClick={() => navigate('/listings')}
                              className="w-full px-4 py-3.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                            >
                              List for Sale
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Seller can cancel listing */}
                          {isSeller && activeListing && activeListing.status === 'active' && (
                            <button
                              onClick={handleCancelListing}
                              disabled={listingLoading}
                              className="w-full px-4 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg disabled:opacity-50"
                            >
                              {listingLoading ? 'Canceling...' : '❌ Cancel Listing'}
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Buyer can purchase listed piece */}
                          {!isOwner && activeListing && activeListing.status === 'active' && 
                           pieceDetailModal.item?.listing_status === 'listed' && (
                            <button
                              onClick={handlePurchaseListing}
                              disabled={listingLoading || !purchaseEligibility.canPurchase}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              {listingLoading ? 'Processing...' : `Buy Now - ${activeListing.price_sol} SOL`}
                            </button>
                          )}
                          
                          {/* FALLBACK: Show buy button for listed pieces even if activeListing hasn't loaded yet */}
                          {!isOwner && !isOwnedByCreator && !activeListing &&
                           pieceDetailModal.item?.listing_status === 'listed' &&
                           pieceDetailModal.item?.listing_price && (
                            <button
                              onClick={() => handleBuyNow(pieceDetailModal.item)}
                              disabled={listingLoading}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              {listingLoading ? 'Loading...' : `Buy Now - ${(pieceDetailModal.item?.listing_price || 0).toFixed(4)} SOL`}
                            </button>
                          )}
                          
                          {/* Show sold status only if viewer is NOT the current owner */}
                          {pieceDetailModal.item?.listing_status === 'sold' && !isOwner && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              ✅ Sold
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Original buttons - Make Offer and Request Trade */}
                    {(() => {
                      const isOwner = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      // Don't show any action buttons if user owns the piece
                      if (isOwner) {
                        return null;
                      }
                      
                      return (
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleMakeOffer(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                          >
                            Make Offer
                          </button>
                          <button 
                            onClick={() => handleRequestTrade(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                          >
                            <ArrowLeftRight size={16} />
                            Request Trade
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                  {pieceCapsuleInfo && (
                    <div className="pt-2">
                      <button 
                        onClick={handleViewCapsule}
                        className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                      >
                        <Package size={14} />
                        View entire capsule
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>

            {/* Item Name */}
            <h4 className="text-white font-medium text-lg mb-4">{buyModal.item?.name}</h4>
            
            {(() => {
              const itemPrice = parseFloat(buyModal.item?.listing_price || buyModal.item?.price || '0');
              if (!itemPrice || itemPrice <= 0) {
                return (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
                    <p className="text-red-400 text-sm">
                      ⚠️ This piece has no price set. Cannot purchase.
                    </p>
                  </div>
                );
              }
              return (
                <>
                  {/* Price Breakdown */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-blue-400">Price</span>
                      <div className="text-right">
                        <div className="text-green-400 font-bold text-lg">{itemPrice.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${(itemPrice * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400">Sale Fee (2.5%)</span>
                      <div className="text-right">
                        <div className="text-orange-400 font-medium">
                          {(itemPrice * 0.025).toFixed(4)} SOL
                        </div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${((itemPrice * 0.025) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-gray-400">Seller Receives</span>
                      <div className="text-right">
                        <div className="text-white font-medium">
                          {(itemPrice * 0.975).toFixed(4)} SOL
                        </div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${((itemPrice * 0.975) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Information */}
                  <div className="mb-6">
                    <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Connected Wallet</span>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} className="text-green-400" />
                          <span className="text-green-400 text-sm font-medium">
                            {isConnected ? 'Connected' : 'Not Connected'}
                          </span>
                        </div>
                      </div>
                      {isConnected && publicKey && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Address</span>
                            <span className="text-white text-sm font-mono">
                              {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-blue-400 text-sm">Balance</span>
                            <div className="text-right">
                              <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                              <div className="text-gray-500 text-xs">
                                ≈ ${(balance * solPrice).toFixed(2)} USD
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Info Message */}
                  <div className="text-gray-300 text-sm mb-6">
                    You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
                  </div>
                </>
              );
            })()}
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.price || '0') || !canWalletTransact(wallet)}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                title={!canWalletTransact(wallet) ? 'Please use Phantom or Solflare wallet' : ''}
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    {!canWalletTransact(wallet) ? 'Wallet Not Supported' : 'Confirm Purchase'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">List Price</span>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{offerModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(offerModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Amount Input */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Your Offer Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerModal.offerAmount}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="mt-2 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Offer Expiration Time */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Offer Expiration</label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-gray-400 text-sm mb-2">Days</label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={offerModal.expirationDays === 0 ? '' : offerModal.expirationDays}
                      onChange={(e) => setOfferModal(prev => ({ ...prev, expirationDays: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-gray-400 text-sm mb-2">Hours</label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={offerModal.expirationHours === 0 ? '' : offerModal.expirationHours}
                      onChange={(e) => setOfferModal(prev => ({ ...prev, expirationHours: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mt-2 text-gray-500 text-sm">
                  Offer will expire in {offerModal.expirationDays} day{offerModal.expirationDays !== 1 ? 's' : ''} and {offerModal.expirationHours} hour{offerModal.expirationHours !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                Your offer will be locked in escrow until the seller accepts or rejects it, or until it expires. You can cancel your offer at any time before expiration.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                title={`Debug: isProcessing=${isProcessing}, isConnected=${isConnected}, offerAmount=${offerModal.offerAmount}, balance=${balance}`}
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Submit Offer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Trade Modal */}
      <RequestTradeModal
        isOpen={requestTradeModal.isOpen}
        onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
        targetPiece={requestTradeModal.item}
        onRequestTrade={handleTradeRequestSubmit}
        onSuccess={handleTradeRequestSuccess}
      />

      {/* Purchase Confirmation Modal for Listings */}
      {showPurchaseConfirmModal && pieceDetailModal.item && activeListing && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => !listingLoading && setShowPurchaseConfirmModal(false)}>
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>

            {/* Item Name */}
            <h4 className="text-white font-medium text-lg mb-4">{pieceDetailModal.item.name}</h4>

            {/* Price Breakdown */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-blue-400">Price</span>
                <div className="text-right">
                  <div className="text-green-400 font-bold text-lg">{activeListing.price_sol?.toFixed(4)} SOL</div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${((activeListing.price_sol || 0) * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400">Sale Fee (2.5%)</span>
                <div className="text-right">
                  <div className="text-orange-400 font-medium">
                    {((activeListing.price_sol || 0) * 0.025).toFixed(4)} SOL
                  </div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${(((activeListing.price_sol || 0) * 0.025) * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-6">
                <span className="text-gray-400">Seller Receives</span>
                <div className="text-right">
                  <div className="text-white font-medium">
                    {((activeListing.price_sol || 0) * 0.975).toFixed(4)} SOL
                  </div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${(((activeListing.price_sol || 0) * 0.975) * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
            </div>

            {/* Wallet Information */}
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3">Wallet Information</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Connected Wallet</span>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-400" />
                    <span className="text-green-400 text-sm font-medium">
                      {isConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                </div>
                {isConnected && publicKey && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Info Message */}
            <div className="text-gray-300 text-sm mb-6">
              You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPurchaseConfirmModal(false)}
                disabled={listingLoading}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurchaseListing}
                disabled={listingLoading || !isConnected}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {listingLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="max-w-full mx-auto px-4 py-8">


        {/* Hero Section - Three Horizontal Carousels */}
        {activeTab !== 'activity' && (
          <div className="mb-16 space-y-12">
            {/* Trending Pieces Carousel - Uses unfiltered data */}
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Trending Pieces</h3>
                  <p className="text-gray-400">Most viewed and favorited pieces this week</p>
                </div>
              </div>
              
              {trendingPieces.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-gray-500 text-sm">No trending pieces yet</p>
                </div>
              ) : (
              <div className="relative overflow-hidden">
                {/* Mobile: Horizontal scroll like Featured Capsules */}
                <div className="md:hidden overflow-x-auto scrollbar-hide">
                  <div className="flex gap-3 pb-4 px-4">
                    {trendingPieces.slice(0, 12).map((item) => {
                      // Check if current user owns this piece
                      const isOwner = isConnected && publicKey && 
                        item?.holder_wallet === publicKey.toString();
                      
                      // Determine if piece is owned by creator (primary sale)
                      const creatorWallet = item?.creator_wallet || 
                                          item?.creators?.wallet ||
                                          item?.originalCreator?.wallet;
                      const isOwnedByCreator = item?.holder_wallet === creatorWallet;
                      
                      // Show buy button only if:
                      // 1. User doesn't own it, AND
                      // 2. (Piece is owned by creator OR piece is listed for resale)
                      const canBuy = !isOwner && (isOwnedByCreator || item?.listing_status === 'listed');
                      
                      // Show trade button only if user doesn't own it
                      const canTrade = !isOwner;
                      
                      return (
                        <div key={item.id} className="w-[160px] flex-shrink-0">
                          <UnifiedPieceCard
                            item={item}
                            onClick={openPieceDetailModal}
                            onBuy={handleBuyNow}
                            onTrade={handleRequestTrade}
                            onFavorite={(item) => toggleFavorite(item.id)}
                            isFavorited={isFavorited(item.id)}
                            solPrice={solPrice}
                            showBuyButton={canBuy}
                            showTradeButton={canTrade}
                            showFavoriteButton={true}
                            showEditionBadges={true}
                            buyButtonText="Buy now"
                            tradeButtonText="Trade"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Desktop: Paginated carousel */}
                <div className="hidden md:block">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${trendingPage * 100}%)` }}
                  >
                    {[0, 1, 2].map((pageIndex) => (
                      <div key={pageIndex} className="w-full flex-shrink-0">
                        <div className="grid gap-6 grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                        {(pageIndex === 0 ? trendingPieces.slice(0, 8) :
                          pageIndex === 1 ? trendingPieces.slice(8, 16) :
                          trendingPieces.slice(16, 24)).map((item) => {
                          // Check if current user owns this piece
                          const isOwner = isConnected && publicKey && 
                            item?.holder_wallet === publicKey.toString();
                          
                          // Determine if piece is owned by creator (primary sale)
                          const creatorWallet = item?.creator_wallet || 
                                              item?.creators?.wallet ||
                                              item?.originalCreator?.wallet;
                          const isOwnedByCreator = item?.holder_wallet === creatorWallet;
                          
                          // Show buy button only if:
                          // 1. User doesn't own it, AND
                          // 2. (Piece is owned by creator OR piece is listed for resale)
                          const canBuy = !isOwner && (isOwnedByCreator || item?.listing_status === 'listed');
                          
                          // Show trade button only if user doesn't own it
                          const canTrade = !isOwner;
                          
                          return (
                            // Use UnifiedPieceCard for consistent design across all pages
                            <UnifiedPieceCard
                              key={item.id}
                              item={item}
                              onClick={openPieceDetailModal}
                              onBuy={handleBuyNow}
                              onTrade={handleRequestTrade}
                              onFavorite={(item) => toggleFavorite(item.id)}
                              isFavorited={isFavorited(item.id)}
                              solPrice={solPrice}
                              showBuyButton={canBuy}
                              showTradeButton={canTrade}
                              showFavoriteButton={true}
                              buyButtonText="Buy now"
                              tradeButtonText="Trade"
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination Dots - Desktop only */}
                <div className="hidden md:flex justify-center space-x-2 mt-6">
                  {[0, 1, 2].map((page) => (
                    <button
                      key={page}
                      onClick={() => setTrendingPage(page)}
                      className={`w-8 h-2 rounded-full transition-all duration-200 ${
                        trendingPage === page
                          ? 'bg-white'
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    />
                  ))}
                </div>
                </div>
              </div>
              )}
            </section>


            {/* Featured Capsules Carousel - Uses unfiltered data */}
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Featured Capsules</h3>
                  <p className="text-gray-400">Curated collections of premium pieces</p>
                </div>
              </div>
              
              <div className="relative overflow-hidden">
                {/* Mobile: Horizontal scroll */}
                <div className="md:hidden overflow-x-auto scrollbar-hide">
                  <div className="flex gap-4 pb-4 px-4">
                    {!capsulesLoading && (capsules || []).slice(0, 8).map((capsule) => (
                      <div 
                        key={capsule.id} 
                        className="w-[280px] flex-shrink-0 group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
                      >
                        {/* Keep existing capsule card content */}
                        {/* Banner Image */}
                        <div className="relative h-32 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                          {capsule.banner ? (
                            <img 
                              src={capsule.banner} 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          ) : capsule.image ? (
                            <img 
                              src={capsule.image} 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-4xl opacity-30">📦</div>
                          )}
                        </div>
                        <div className="p-3 space-y-2">
                          <h3 className="text-white font-bold text-sm line-clamp-1">
                            {capsule.name}
                          </h3>
                          <p className="text-gray-400 text-xs line-clamp-2">
                            {capsule.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Desktop: Paginated carousel */}
                <div className="hidden md:block">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${capsulePage * 100}%)` }}
                  >
                    {[0, 1, 2].map((pageIndex) => (
                      <div key={pageIndex} className="w-full flex-shrink-0">
                        <div className="grid gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {!capsulesLoading && (pageIndex === 0 ? (capsules || []).slice(0, 4) :
                          pageIndex === 1 ? (capsules || []).slice(4, 8) :
                          (capsules || []).slice(8, 12)).map((capsule) => (
                  <div 
                    key={capsule.id} 
                    className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
                  >
                    <div 
                      onClick={() => navigate(`/capsule/${capsule.id}`)}
                      className="cursor-pointer group relative h-full flex flex-col"
                    >
                      {/* Banner Image - Top half */}
                      <div className="relative h-1/2 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                        {capsule.banner ? (
                          <img 
                            src={capsule.banner} 
                            alt={capsule.name}
                            className="w-full h-full object-cover"
                          />
                        ) : capsule.image ? (
                          <img 
                            src={capsule.image} 
                            alt={capsule.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img 
                            src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=200&fit=crop" 
                            alt={capsule.name}
                            className="w-full h-full object-cover"
                          />
                        )}

                        {/* Heart Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCapsuleFavorite(capsule);
                          }}
                          className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        >
                          <Heart 
                            size={16} 
                            className={`${
                              isCapsuleFavorited(capsule.id) 
                                ? 'text-red-500 fill-red-500' 
                                : 'text-white/70 hover:text-red-500'
                            } transition-all duration-300`} 
                          />
                        </button>
                      </div>

                      {/* Bottom Section with Info - Bottom half */}
                      <div className="h-1/2 p-3 flex flex-col justify-between">
                        <div className="space-y-1">
                          {/* Title */}
                          <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                            {capsule.name}
                          </h3>

                          {/* Description */}
                          <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
                            {capsule.description}
                          </p>

                          {/* Pieces Count */}
                          <div className="text-gray-400 text-xs">
                            • {capsule.pieceCount || capsule.piece_count || 0} {(capsule.pieceCount || capsule.piece_count || 0) === 1 ? 'piece' : 'pieces'}
                          </div>
                        </div>

                        <div className="space-y-1">
                          {/* Avg Price Row */}
                          <div className="flex items-center justify-between">
                            <div className="text-gray-400 text-xs">Avg Price</div>
                            <div className="text-right">
                              <div className="text-green-400 font-semibold text-sm">
                                {(() => {
                                  const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                  // Show more precision for prices < 0.1 SOL
                                  if (price > 0 && price < 0.1) {
                                    return price.toFixed(3);
                                  }
                                  return isNaN(price) ? '0.0' : price.toFixed(1);
                                })()} SOL
                              </div>
                              <div className="text-gray-500 text-xs">
                                {(() => {
                                  const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                  const usdPrice = isNaN(price) ? 0 : price * solPrice;
                                  return usdPrice > 0 ? `~$${usdPrice.toFixed(2)} USD` : '';
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Drop Time */}
                          <div className="text-gray-500 text-xs">
                            Dropped {(() => {
                              const dropDate = capsule.dropped_at || capsule.created_at;
                              if (!dropDate) return '09/09/2025';
                              return new Date(dropDate).toLocaleDateString('en-US', { 
                                month: '2-digit', 
                                day: '2-digit', 
                                year: 'numeric' 
                              });
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pagination Dots - Desktop only */}
              <div className="hidden md:flex justify-center space-x-2 mt-6">
                {[0, 1, 2].map((page) => (
                  <button
                    key={page}
                    onClick={() => setCapsulePage(page)}
                    className={`w-8 h-2 rounded-full transition-all duration-200 ${
                      capsulePage === page
                        ? 'bg-white'
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
              </div>
            </section>

            {/* Top Creators Carousel - Uses real data from database */}
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Top Creators</h3>
                  <p className="text-gray-400">Most successful creators on the platform</p>
                </div>
              </div>
              
              {creatorsLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  <p className="text-gray-400 mt-4">Loading creators...</p>
                </div>
              ) : topCreators && topCreators.length > 0 ? (
              <div className="relative overflow-hidden">
                {/* Mobile: Horizontal scroll */}
                <div className="md:hidden overflow-x-auto scrollbar-hide">
                  <div className="flex gap-4 pb-4 px-4">
                    {topCreators.slice(0, 10).map((creator) => (
                      <div 
                        key={creator.id}
                        onClick={() => navigate(creator.link || `/creator/${creator.name}`)}
                        className="w-[200px] flex-shrink-0 group relative bg-[#1a1a1a] border border-white/30 rounded-xl p-4 hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          {/* Avatar */}
                          <div className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center border border-gray-600/30 overflow-hidden">
                            {creator.avatar ? (
                              <img 
                                src={creator.avatar} 
                                alt={creator.name} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                                <span className="text-white font-bold text-xl">
                                  {creator.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <h3 className="text-white text-base font-medium truncate">
                                {creator.name}
                              </h3>
                              {creator.verified && (
                                <VerifiedBadge 
                                  size="sm"
                                  verificationType={creator.verificationType || 'none'}
                                />
                              )}
                            </div>
                            <p className="text-gray-400 text-sm truncate">
                              {creator.handle}
                            </p>
                          </div>
                        </div>
                        
                        {/* Stats */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">Sales</span>
                            <span className="text-white font-medium">{creator.salesCount || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Desktop: Paginated carousel */}
                <div className="hidden md:block">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${creatorPage * 100}%)` }}
                  >
                    {[0, 1, 2].map((pageIndex) => (
                      <div key={pageIndex} className="w-full flex-shrink-0">
                        <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-4">
                          {topCreators.slice(pageIndex * 5, (pageIndex + 1) * 5).map((creator) => (
                            <div 
                              key={creator.id}
                              onClick={() => navigate(creator.link || `/creator/${creator.name}`)}
                              className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl p-3 hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer flex-shrink-0"
                              style={{ width: 'calc(20% - 12.8px)' }}
                            >
                              <div className="flex items-center gap-3 mb-3">
                                {/* Avatar */}
                                <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center border border-gray-600/30 overflow-hidden">
                                  {creator.avatar ? (
                                    <img 
                                      src={creator.avatar} 
                                      alt={creator.name} 
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                                      <span className="text-white font-bold text-lg">
                                        {creator.name.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1">
                                    <h3 className="text-white text-sm font-medium truncate">
                                      {creator.name}
                                    </h3>
                                    {creator.verified && (
                                      <VerifiedBadge 
                                        size="sm"
                                        verificationType={creator.verificationType || 'none'}
                                      />
                                    )}
                                  </div>
                                  <p className="text-gray-400 text-xs truncate">
                                    {creator.handle}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Stats */}
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-400">Sales</span>
                                  <span className="text-white font-medium">{creator.salesCount || 0}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                
                  {/* Pagination Dots - Desktop only */}
                  <div className="flex justify-center space-x-2 mt-6">
                    {[0, 1, 2].map((page) => (
                      <button
                        key={page}
                        onClick={() => setCreatorPage(page)}
                        className={`w-8 h-2 rounded-full transition-all duration-200 ${
                          creatorPage === page
                            ? 'bg-white'
                            : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No creators found. Be the first to create!</p>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Search + Filters Section */}
        {activeTab !== 'activity' && (
          <div className="mb-8 space-y-6">
            {/* Big Search Bar */}
            <div className="relative">
              <div className="hidden md:flex absolute inset-y-0 left-0 pl-4 items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Search ${activeTab === 'pieces' ? 'pieces' : 'capsules'}, creators...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 md:pl-14 pr-4 py-3 md:py-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/20 text-sm md:text-base"
              />
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap gap-4 items-center">
              {/* Category Filter - Only show for pieces */}
              {activeTab === 'pieces' && (
                <div className="flex items-center gap-2">
                  <label className="text-gray-300 text-sm font-medium">Category:</label>
                  <div className="relative" ref={categoryDropdownRef}>
                  <button
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{categoryOptions.find(opt => opt.id === categoryFilter)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isCategoryDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {categoryOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleCategoryChange(option.id)}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 hover:bg-[#1a1a1a] ${
                            categoryFilter === option.id ? 'text-white bg-[#1a1a1a]' : 'text-gray-300'
                          }`}
                        >
                          {option.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Price Range Filter */}
              <div className="flex items-center gap-2">
                <label className="text-gray-300 text-sm font-medium">Price:</label>
                <div className="relative" ref={priceDropdownRef}>
                  <button
                    onClick={() => setIsPriceDropdownOpen(!isPriceDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{priceOptions.find(opt => opt.id === priceFilter)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isPriceDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isPriceDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {priceOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handlePriceChange(option.id)}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 hover:bg-[#1a1a1a] ${
                            priceFilter === option.id ? 'text-white bg-[#1a1a1a]' : 'text-gray-300'
                          }`}
                        >
                          {option.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Verified Only Filter */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                    className="sr-only" 
                  />
                  <div 
                    onClick={() => setVerifiedOnly(!verifiedOnly)}
                    className={`w-5 h-5 border-2 rounded-md transition-all duration-200 relative cursor-pointer hover:border-gray-400 ${
                      verifiedOnly 
                        ? 'border-gray-400 bg-transparent' 
                        : 'border-gray-500 bg-[#1a1a1a]'
                    }`}
                  >
                    {verifiedOnly && (
                      <img 
                        src="/images/Verification badge.png" 
                        alt="Verified" 
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                    )}
                  </div>
                </div>
                <span className="text-gray-300 text-sm">Verified Only</span>
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-gray-300 text-sm font-medium">Sort by:</label>
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isSortDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleSortChange(option.id)}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 hover:bg-[#1a1a1a] ${
                            sortBy === option.id ? 'text-white bg-[#1a1a1a]' : 'text-gray-300'
                          }`}
                        >
                          {option.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Grid - All Items with Filters Applied */}
        {activeTab !== 'activity' && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {activeTab === 'pieces' ? 'All Pieces' : 'All Capsules'}
                </h3>
              </div>
            </div>
            
            <div className={`grid gap-6 ${activeTab === 'capsules' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8'}`}>
              {capsulesLoading && activeTab === 'capsules' ? (
                <div className="col-span-full flex items-center justify-center py-12">
                  <div className="text-white text-lg">Loading capsules...</div>
                </div>
              ) : (
                getFilteredItems().map((item) => {
                console.log('Rendering piece:', item.name);
                console.log('  item.thumbnailUrl:', item.thumbnailUrl);
                
                // Render capsules with wrapper div, pieces without wrapper
                if (activeTab === 'capsules') {
                  return (
                    <div 
                      key={item.id} 
                      className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20"
                    >
                      {/* Capsule Card Design */}
                      {/* Banner Image - Full width, no padding */}
                      <div className="relative aspect-[1400/400] bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                        {item.banner ? (
                          <img 
                            src={item.banner} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-4xl opacity-30">📦</div>
                        )}
                        
                        {/* Status Tag - Overlay on banner */}
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-0.5 bg-white text-black text-xs rounded-full" style={{color: '#111827'}}>
                            Capsule
                          </span>
                        </div>
                      </div>

                      {/* Bottom Section with Info */}
                      <div className="p-3 space-y-2">
                        {/* Title with Number */}
                        <h3 className="text-white font-bold text-sm line-clamp-1">
                          {item.name} {item.capsuleNumber}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-400 text-xs line-clamp-2">
                          {item.description || `A curated collection of ${item.pieceCount || item.piece_count || 0} digital fashion pieces with modern styling and premium quality.`}
                        </p>

                        {/* Stats Row */}
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                              {(() => {
                                const count = item.pieceCount || item.piece_count || 0;
                                console.log('🔢 Piece count debug for', item.name, ':', { pieceCount: item.pieceCount, piece_count: item.piece_count, final: count });
                                return `${count} ${count === 1 ? 'piece' : 'pieces'}`;
                              })()}
                            </span>
                          </div>
                        </div>

                        {/* Price Row */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-400">Avg Price</div>
                            <div className="text-green-400 font-semibold text-sm">
                              {(() => {
                                const price = parseFloat(item.avgPrice || item.price || 0);
                                console.log('💰 Explore capsule price debug:', { name: item.name, avgPrice: item.avgPrice, price: item.price, calculated: price });
                                return isNaN(price) ? '0.0' : price.toFixed(1);
                              })()} SOL
                            </div>
                          </div>
                          <div className="text-right text-xs text-gray-400">
                            {(() => {
                              const price = parseFloat(item.avgPrice || item.price || 0);
                              const usdPrice = isNaN(price) ? 0 : price * solPrice;
                              return usdPrice > 0 ? `~$${usdPrice.toFixed(2)} USD` : '';
                            })()}
                          </div>
                        </div>

                        {/* Drop Date */}
                        <div className="text-xs text-gray-500">
                          Dropped {item.dropDate ? new Date(item.dropDate).toLocaleDateString('en-US', { 
                            year: '2-digit', 
                            month: '2-digit', 
                            day: '2-digit' 
                          }) : item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { 
                            year: '2-digit', 
                            month: '2-digit', 
                            day: '2-digit' 
                          }) : 'Recently'}
                        </div>
                      </div>
                      
                      {/* Heart Icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCapsuleFavorite(item);
                        }}
                        className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                      >
                        <Heart 
                          size={16} 
                          className={`${
                            isCapsuleFavorited(item.id) 
                              ? 'text-red-500 fill-red-500' 
                              : 'text-white/70 hover:text-red-500'
                          } transition-all duration-300`} 
                        />
                      </button>
                      
                      {/* Hover View Capsule Button - Slides up from bottom */}
                      <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-all duration-300 ease-out p-3">
                        <button
                          onClick={() => handleViewCapsuleFromCard(item)}
                          className="w-full bg-white hover:bg-gray-200 text-gray-900 py-2 px-3 rounded-lg font-semibold transition-all duration-200 text-sm"
                          style={{color: '#111827'}}
                        >
                          View Capsule
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  // Render pieces directly without wrapper - UnifiedPieceCard has its own styling
                  // Check if current user owns this piece
                  const isOwner = isConnected && publicKey && 
                    item?.holder_wallet === publicKey.toString();
                  
                  // Determine if piece is owned by creator (primary sale)
                  const creatorWallet = item?.creator_wallet || 
                                      item?.creators?.wallet ||
                                      item?.originalCreator?.wallet;
                  const isOwnedByCreator = item?.holder_wallet === creatorWallet;
                  
                  // Show buy button only if:
                  // 1. User doesn't own it, AND
                  // 2. (Piece is owned by creator OR piece is listed for resale)
                  const canBuy = !isOwner && (isOwnedByCreator || item?.listing_status === 'listed');
                  
                  // Show trade button only if user doesn't own it
                  const canTrade = !isOwner;
                  
                  return (
                    <UnifiedPieceCard
                      key={item.id}
                      item={item}
                      onClick={openPieceDetailModal}
                      onBuy={handleBuyNow}
                      onTrade={handleRequestTrade}
                      onFavorite={(item) => toggleFavorite(item.id)}
                      isFavorited={isFavorited(item.id)}
                      solPrice={solPrice}
                      showBuyButton={canBuy}
                      showTradeButton={canTrade}
                      showFavoriteButton={true}
                      buyButtonText="Buy now"
                      tradeButtonText="Trade"
                    />
                  );
                }
              })
              )}
            </div>

            {/* Infinite Scroll Loading Indicator */}
            {activeTab === 'pieces' && (
              <div ref={loadMoreRef} className="col-span-full py-8">
                {isLoadingMore && hasMorePieces && (
                  <div className="flex items-center justify-center">
                    <div className="text-white text-lg flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      Loading more pieces...
                    </div>
                  </div>
                )}
                {!hasMorePieces && allLoadedPieces.length > 0 && (
                  <div className="text-center text-gray-400 text-sm">
                    You've reached the end! All pieces loaded.
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {!capsulesLoading && getFilteredItems().length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-2xl font-bold text-white mb-2">No {activeTab} found</h3>
                <p className="text-gray-400 mb-6">
                  {searchQuery ? `No ${activeTab} found for "${searchQuery}"` :
                   categoryFilter !== 'all' ? `No ${activeTab} found in ${categoryOptions.find(opt => opt.id === categoryFilter)?.name}` :
                   priceFilter !== 'all' ? `No ${activeTab} found in ${priceOptions.find(opt => opt.id === priceFilter)?.name}` :
                   verifiedOnly ? `No verified ${activeTab} found` :
                   `No ${activeTab} available`}
                </p>
                <button 
                  onClick={() => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                    setPriceFilter('all');
                    setVerifiedOnly(false);
                    setSortBy('newest');
                  }}
                  className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-semibold transition-all duration-200 shadow-lg"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}



        {/* Content Area */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-2xl font-bold text-white mb-2">Activity Feed</h3>
              <p className="text-gray-400 mb-6">Track your recent transactions and marketplace activity</p>
              <button className="px-6 py-3 bg-white hover:bg-gray-200 text-black rounded-xl font-semibold transition-all duration-200">
                View All Activity
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 z-[70] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-green-600 border border-green-500/30 rounded-xl p-4 shadow-lg max-w-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle size={14} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{successMessage}</p>
              </div>
              <button
                onClick={() => setShowSuccessNotification(false)}
                className="text-white/70 hover:text-white transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Not For Sale Modal */}
      {notForSaleModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={32} className="text-red-500" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-white mb-2">
                Not Available for Purchase
              </h3>
              
              {/* Message */}
              <p className="text-gray-400 mb-6 leading-relaxed">
                This piece is not currently for sale. The holder hasn't listed it for sale, but you can make an offer or request a trade!
              </p>
              
              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => setNotForSaleModal({ isOpen: false, item: null })}
                  className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Listing Confirmation Modal */}
      {cancelListingModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertCircle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Cancel Listing</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to cancel this listing?</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">{cancelListingModal.item?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Listed for {activeListing?.price_sol || cancelListingModal.item?.price} SOL
                </div>
              </div>
              <div className="mt-4 text-gray-300 text-sm">
                This will remove the piece from the marketplace. You can list it again later.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setCancelListingModal({ isOpen: false, item: null })}
                disabled={listingLoading}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Keep Listing
              </button>
              <button
                onClick={confirmCancelListing}
                disabled={listingLoading}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {listingLoading ? 'Cancelling...' : 'Cancel Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List Modal */}
      {showListModal && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
          onClick={() => setShowListModal(false)}
        >
          <div 
            className="bg-[#1a1a1a] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4">
              List {pieceDetailModal.item?.name} for Sale
            </h3>
            
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">
                Price (SOL)
              </label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={listPrice}
                onChange={(e) => setListPrice(e.target.value)}
                placeholder="Enter price in SOL"
                disabled={listingLoading}
                className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCreateListing}
                disabled={listingLoading || !listPrice}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {listingLoading ? 'Listing...' : 'List for Sale'}
              </button>
              <button
                onClick={() => setShowListModal(false)}
                disabled={listingLoading}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
            
            <div className="mt-4 p-3 bg-black/50 border border-gray-800 rounded-lg">
              <p className="text-gray-400 text-xs">
                ℹ️ You'll be asked to approve a delegate authorization, allowing the marketplace
                to transfer this piece when sold.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Grouped Pieces Modal */}
      <GroupedPiecesModal
        isOpen={groupedPiecesModal.isOpen}
        onClose={() => setGroupedPiecesModal({ isOpen: false, groupedCard: null })}
        groupedCard={groupedPiecesModal.groupedCard}
        solPrice={solPrice}
        wallet={wallet}
        publicKey={publicKey}
        isConnected={isConnected}
        onBuyNow={(selectedPiece) => {
          // Trigger buy flow for selected piece
          handleBuyNow(selectedPiece);
          setGroupedPiecesModal({ isOpen: false, groupedCard: null });
        }}
        onMakeOffer={(selectedPiece) => {
          // Trigger offer flow for selected piece
          handleMakeOffer(selectedPiece);
          setGroupedPiecesModal({ isOpen: false, groupedCard: null });
        }}
        onRequestTrade={(selectedPiece) => {
          // Trigger trade flow for selected piece
          handleRequestTrade(selectedPiece);
          setGroupedPiecesModal({ isOpen: false, groupedCard: null });
        }}
      />

      {/* Wallet Connection Modal */}
      <WalletConnectionModal
        isOpen={showWalletModal}
        onClose={closeWalletModal}
        message={walletModalMessage}
      />

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : notification.type === 'error' ? (
                  <AlertCircle size={20} className="text-red-400" />
                ) : (
                  <AlertCircle size={20} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Explore; 